<script setup lang="ts">
// Vue & Plugins
import { computed, Ref, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// Components
import AreaSelect from '../../areas/components/inputs/AreaSelect.vue'

// Composables
import { useShiftCreateMutation } from '../composables/useShift'
import { ResourceKeys, useResourceNames } from '../../common/composables/resource-names'

// Models
import type { SiteID } from '../../sites/models/site'
import { Area } from '../../areas/models/area'

// Services
import { Employee } from '../../employees/models/employee'
import { DateTime } from 'luxon'
import { ShiftReason } from '../../shift_reasons/models/shiftReason'
import { Pagination } from '../../common/models/pagination'
import { useQuery } from '@tanstack/vue-query'
import { eligibleQueryOptions } from '../../employees/composables/useEmployeeQuery'
import DateTimePicker from '../../common/components/inputs/DateTimePicker.vue'
import ShiftReasonDropdown from '../../shift_reasons/components/ShiftReasonDropdown.vue'
import CurrencyInput from '../../common/components/inputs/CurrencyInput.vue'
import { refDebounced } from '@vueuse/core'

const { t } = useI18n({ inheritLocale: true })
const { getSingular } = useResourceNames()

const { siteId } = defineProps<{
    siteId: SiteID
}>()

type ShiftAssignmentForm = {
  startDate: DateTime
  endDate: DateTime
  area: Area | null
  assignee: Employee | null
  rateAmount: number | undefined
  reason: ShiftReason | null
  note: string
}

const getInitialState = (): ShiftAssignmentForm => ({
  startDate: DateTime.now().set({ hour: 8, minute: 0 }),
  endDate: DateTime.now().set({ hour: 20, minute: 0 }),
  area: null,
  assignee: null,
  rateAmount: undefined,
  reason: null,
  note: '',
})
const formRef: Ref<ShiftAssignmentForm> = ref(getInitialState())

const areaId = computed(() => formRef.value.area?.id)

const hasArea = computed(() => formRef.value.area !== null)

const {
    mutate: createShift,
    isPending: isCreatePending,
} = useShiftCreateMutation(() => siteId)
const createPressed = () => {
  if (formRef.value.area === null) {
    return
  }

  createShift(
    {
      form: {
        startDate: formRef.value.startDate,
        endDate: formRef.value.endDate,
        areaId: formRef.value.area.id,
        assigneeId: formRef.value.assignee?.id,
        rateAmount: formRef.value.rateAmount,
        reasonId: formRef.value.reason?.id,
        note: formRef.value.note,
        published: true,
      }
    },
  )
}

const resetPressed = () => {
  formRef.value = getInitialState()
}

const hasChanges = computed(() => formRef.value.area && formRef.value.assignee)
const isSaveDisabled = computed(() => !hasChanges.value)

const areaTerm = computed(() => getSingular(ResourceKeys.AREA).value)
const isAssigneeShown = ref(false)
const isAssigneeQueryEnabled = computed(() => hasArea.value && isAssigneeShown.value)
const assigneeTooltip = computed(() => hasArea.value ? '' : `Select a ${areaTerm.value} to enable assignee selection`)
const nameInput = ref('')
const name = refDebounced(nameInput, 500)
const {
  data: eligibleData,
} = useQuery<{ employees: Employee[], pagination: Pagination }, Error>(
  {
    ...eligibleQueryOptions(() => siteId, {
      areaId: areaId,
      startDate: formRef.value.startDate,
      endDate: formRef.value.endDate,
      name,
    }),
    enabled: isAssigneeQueryEnabled,
  }
)
const eligible = computed(() => eligibleData.value?.employees ?? [])

const content = computed(() => {
    return {
        fields: {
          area: {
            label: getSingular(ResourceKeys.AREA).value,
          },

          startDate: {
            label: 'Start Date',
          },

          endDate: {
            label: 'End Date',
          },

          assignee: {
            label: 'Assignee',
          },

          rate: {
            label: 'Rate',
          },

          note: {
            label: 'Note',
          },

          reason: {
            label: 'Reason',
          },
        },

        buttons: {
            create: t('resources.buttons.create'),
        },
    }
})
</script>

<template>
  <div
    class="flex flex-col gap-y-4 m-1"
  >
    <div class="flex flex-col gap-2">
      <label for="area-input">{{ content.fields.area.label }}</label>
      <AreaSelect
        v-model:selected="formRef.area"
        :site-id="siteId"
        class="w-full"
      />
    </div>

    <div
      class="flex gap-2"
    >
      <div class="w-full flex flex-col gap-2">
        <label for="start-date-input">{{ content.fields.startDate.label }}</label>
        <div>
          <DateTimePicker
            v-model:selected="formRef.startDate"
            show-time
          />
        </div>
      </div>
      <div class="w-full flex flex-col gap-2">
        <label for="end-date-input">{{ content.fields.endDate.label }}</label>
        <div>
          <DateTimePicker
            v-model:selected="formRef.endDate"
            show-time
          />
        </div>
      </div>
    </div>

    <div
      v-tooltip.left="assigneeTooltip"
      class="flex flex-col gap-2"
    >
      <label for="assignee-input">{{ content.fields.assignee.label }}</label>
      <Select
        v-model="formRef.assignee"
        :options="eligible"
        filter
        :filter-fields="['firstName', 'lastName', 'email']"
        option-label="displayName"
        placeholder="Assign To"
        class="w-full"
        :disabled="!hasArea"
        @filter="nameInput = $event.value"
        @show="isAssigneeShown = true"
        @hide="isAssigneeShown = false"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label for="rate-input">{{ content.fields.rate.label }}</label>
      <CurrencyInput
        v-model:value="formRef.rateAmount"
        class="w-full"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label for="note-input">{{ content.fields.note.label }}</label>
      <InputText
        v-model="formRef.note"
        class="w-full"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label for="reason-input">{{ content.fields.reason.label }}</label>
      <ShiftReasonDropdown
        v-model="formRef.reason"
        class="w-full"
      />
    </div>

    <div
      class="flex justify-end gap-x-2"
    >
      <Button
        :label="content.buttons.create"
        :loading="isCreatePending"
        :disabled="isSaveDisabled"
        @click="createPressed"
      />

      <Button
        icon="pi pi-undo"
        text
        @click="resetPressed"
      />
    </div>
  </div>
</template>